import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { s } from "@/i18n/translations";

const Stat = ({ value, label }) => (
  <div className="border-t-2 border-[#0A0A0A] pt-4" data-testid={`stat-${label.slice(0, 4)}`}>
    <div className="font-display text-5xl md:text-6xl leading-none text-[#0A0A0A]">{value}</div>
    <div className="mt-2 text-xs uppercase tracking-widest text-[#0A0A0A]/60">{label}</div>
  </div>
);

// Hardcoded YouTube "Perla Music in una parola"
const PERLA_ONE_WORD_ID = "B_JzYMGvlU0";

// Fallback testimonials — usati per riempire la griglia a 4 quando il DB ne ha meno
const FALLBACK_TESTIMONIALS = [
  {
    id: "fallback-luca",
    kind: "text",
    author: "Luca S.",
    role_it: "Producer & Artista",
    role_en: "Producer & Artist",
    text_it: "Con Perla ho trovato quello che cercavo da anni: gente competente che ci mette la faccia. Rispondono, decidono in fretta e ti fanno sentire un progetto, non un numero.",
    text_en: "With Perla I finally found what I was looking for: real professionals who show up. They answer, they decide fast, and they treat you like a project, not a number.",
  },
];

const ServiceCard = ({ num, title, text }) => (
  <div className="group relative p-8 border-2 border-[#0A0A0A] bg-[#FAFAF7] hover:bg-[#0A0A0A] hover:text-[#FAFAF7] transition-colors duration-500" data-testid={`service-card-${num}`}>
    <div className="text-xs font-bold tracking-widest text-[#FF5A1F]">{num}</div>
    <h3 className="mt-4 font-display text-3xl tracking-wide uppercase">{title}</h3>
    <p className="mt-4 font-body text-sm leading-relaxed opacity-80">{text}</p>
    <ArrowUpRight className="absolute top-6 right-6 opacity-40 group-hover:opacity-100 transition" size={20} />
  </div>
);

const getYouTubeId = (url = "") => {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : "";
};

export default function Home() {
  const { t, lang } = useLang();
  const [testimonials, setTestimonials] = useState([]);

  const cmsVideo = null;
  void cmsVideo;
  // Video "Perla Music in una parola" — fissato dal cliente
  const featuredVideoId = PERLA_ONE_WORD_ID;
  const featuredCaption = "Perla Music";

  const textQuotes = useMemo(() => {
    const fromDb = testimonials.filter((x) => x.kind !== "video");
    if (fromDb.length >= 4) return fromDb.slice(0, 4);
    const needed = 4 - fromDb.length;
    return [...fromDb, ...FALLBACK_TESTIMONIALS.slice(0, needed)];
  }, [testimonials]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 pt-12 lg:pt-16 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 fade-up">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-6" data-testid="hero-eyebrow">
              {t("home.hero.eyebrow", "PERLA MUSIC MANAGEMENT")}
            </div>
            <h1 className="font-display text-[16vw] sm:text-[13vw] lg:text-[10vw] leading-[0.85] tracking-tight uppercase text-[#0A0A0A]" data-testid="hero-headline">
              <span className="block">{t("home.hero.line1", "MUSICA.")}</span>
              <span className="block">{t("home.hero.line2", "TALENTO.")}</span>
              <span className="block text-gradient-arancio">{t("home.hero.line3", "FUTURO.")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-[#0A0A0A]/80 leading-relaxed" data-testid="hero-subtitle">
              {t("home.hero.subtitle", "")}
            </p>
          </div>

          {/* Hero video (desktop only) — live autoplay reel */}
          <div className="hidden lg:block lg:col-span-4 fade-up" data-testid="hero-live-video">
            <div className="relative aspect-[3/4] overflow-hidden border-2 border-[#0A0A0A] bg-[#0A0A0A] arancio-glow">
              <video
                src="/assets/live-autoplay.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid="hero-live-video-player"
              />
            </div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 text-right">
              {t("home.hero.video_caption", "I nostri artisti live")}
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 flex items-center gap-2 pointer-events-none">
          <ArrowDown size={12} className="animate-bounce" /> {s("common.scroll", lang)}
        </div>
      </section>

      {/* ===== 1. "Perla Music in poche parole" ===== */}
      <section className="bg-[#F2F2EC] border-y-2 border-[#0A0A0A] py-20 lg:py-28" data-testid="home-testimonials-section">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          {/* Row 1: Title LEFT + Words graphic RIGHT (desktop) */}
          <div className="grid lg:grid-cols-12 gap-8 items-center mb-12">
            <div className="lg:col-span-5">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">
                {t("home.testimonials.eyebrow", "— Perla Music in poche parole")}
              </div>
              <h2 className="font-display text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.85] text-[#0A0A0A]">
                {t("home.testimonials.title", "Perla Music in poche parole")}
              </h2>
              <p className="mt-6 text-lg text-[#0A0A0A]/70">
                {t("home.testimonials.subtitle", "")}
              </p>
            </div>

            {/* Words graphic — desktop only, same row as title */}
            <div className="hidden lg:block lg:col-span-7">
              <div className="relative bg-white overflow-hidden" data-testid="home-words-graphic">
                <img
                  src="/assets/perla-words.jpg"
                  alt="Perla Music — famiglia, positivo, opportunità, team, visionari, speranza"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>

          {/* Row 2: Vertical video LEFT + 4 quotes RIGHT */}
          <div className="grid lg:grid-cols-12 gap-6 items-stretch">
            <div className="lg:col-span-5">
              <div className="relative aspect-[9/16] border-2 border-[#0A0A0A] overflow-hidden bg-[#0A0A0A] max-h-[560px] mx-auto lg:mx-0" data-testid="home-featured-video">
                <iframe
                  src={`https://www.youtube.com/embed/${featuredVideoId}?rel=0`}
                  title="Perla Music in una parola"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
              <div className="mt-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">
                {t("home.testimonials.featured_prefix", "In evidenza")} · {featuredCaption}
              </div>
            </div>

            {/* Quotes grid 2×2 = 4 testimonials */}
            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4 h-full">
                {textQuotes.map((q, i) => (
                  <div key={q.id} className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7] flex flex-col" data-testid={`home-testimonial-${i}`}>
                    <div className="text-4xl font-display text-[#FF5A1F] leading-none">"</div>
                    <p className="mt-2 text-sm leading-relaxed text-[#0A0A0A]/85 flex-1">
                      {q[`text_${lang}`] || q.text_it}
                    </p>
                    <div className="mt-3 pt-2 border-t border-[#0A0A0A]/20">
                      <div className="font-display text-base uppercase text-[#0A0A0A]">{q.author}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">{q[`role_${lang}`] || q.role_it}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 flex justify-center lg:justify-end">
            <Link to="/testimonianze" className="btn-outline-dark py-3 px-6 text-xs" data-testid="home-view-testimonials">
              {t("home.testimonials.cta", "Guarda tutte le testimonianze")}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 2. Mini "Chi siamo" ===== */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 lg:py-28" data-testid="home-about-mini">
        <div className="max-w-4xl">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">
            {t("home.about_mini.eyebrow", "— Chi siamo")}
          </div>
          <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.9] text-[#0A0A0A]">
            {t("home.about_mini.title", "")}
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#0A0A0A]/75">
            {t("home.about_mini.text", "")}
          </p>
          <div className="mt-8">
            <Link to="/chi-siamo" className="btn-outline-dark" data-testid="home-about-cta">
              {t("home.about_mini.cta", "Scopri chi siamo")}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== 3. Stats ===== */}
      <section className="border-y-2 border-[#0A0A0A] bg-[#F2F2EC]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid grid-cols-2 lg:grid-cols-4 gap-10">
          <Stat value={t("stats.artists_count", "24")} label={t("home.stats.artists", "Artisti")} />
          <Stat value={t("stats.streams_count", "80M+")} label={t("home.stats.streams", "Streaming")} />
          <Stat value={t("stats.releases_count", "180")} label={t("home.stats.releases", "Release")} />
          <Stat value={t("stats.years_count", "7")} label={t("home.stats.years", "Anni")} />
        </div>
      </section>

      {/* ===== 4. Servizi ===== */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-5">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-4">— Servizi</div>
            <h2 className="font-display text-6xl lg:text-7xl uppercase leading-[0.9] text-[#0A0A0A]">
              {t("services.title", "Servizi")}
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex items-end">
            <p className="text-lg text-[#0A0A0A]/70">{t("services.subtitle", "")}</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-0 border-t-2 border-l-2 border-[#0A0A0A]">
          <div className="border-r-2 border-b-2 border-[#0A0A0A] -ml-[2px] -mt-[2px]"><ServiceCard num="01" title={t("services.management.title", "Management")} text={t("services.management.text", "")} /></div>
          <div className="border-r-2 border-b-2 border-[#0A0A0A] -mt-[2px]"><ServiceCard num="02" title={t("services.production.title", "Produzione")} text={t("services.production.text", "")} /></div>
          <div className="border-r-2 border-b-2 border-[#0A0A0A] -ml-[2px]"><ServiceCard num="03" title={t("services.distribution.title", "Distribuzione")} text={t("services.distribution.text", "")} /></div>
          <div className="border-r-2 border-b-2 border-[#0A0A0A]"><ServiceCard num="04" title={t("services.promo.title", "Promo")} text={t("services.promo.text", "")} /></div>
        </div>
      </section>

      {/* ===== Big CTA ===== */}
      <section className="bg-[#FF5A1F] py-24">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display text-5xl md:text-7xl uppercase text-[#FAFAF7] leading-[0.9]">
            Pronto a costruire<br /> il tuo futuro?
          </h2>
          <Link to="/contatti" className="bg-[#0A0A0A] text-[#FAFAF7] px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#1A1A1A] flex items-center gap-2" data-testid="home-bottom-cta">
            {t("home.hero.cta", "CANDIDATI")} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
